'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type Step = 'welcome' | 'rating' | 'nps' | 'questions' | 'open' | 'complete';

interface SurveyData {
  overallRating: number;
  npsScore: number | null;
  serviceRatings: Record<string, number>;
  answers: Record<string, string>;
  openFeedback: string;
  email: string;
}

const serviceCategories = [
  { key: 'booking_ease', label: 'Ease of Booking', icon: 'TicketIcon' },
  { key: 'price_value', label: 'Price & Value', icon: 'CurrencyPoundIcon' },
  { key: 'customer_support', label: 'Customer Support', icon: 'ChatBubbleLeftRightIcon' },
  { key: 'website_experience', label: 'Website Experience', icon: 'ComputerDesktopIcon' },
];

const multiChoiceQuestions = [
  {
    key: 'visit_purpose',
    question: 'What was the primary purpose of your trip?',
    options: ['Business Travel', 'Leisure / Holiday', 'Visiting Family & Friends', 'Study / Education', 'Other'],
  },
  {
    key: 'booking_frequency',
    question: 'How often do you book flights?',
    options: ['First time', 'Once a year', '2–4 times a year', '5+ times a year'],
  },
  {
    key: 'recommend_likelihood',
    question: 'Would you use Dial For Flight again?',
    options: ['Definitely yes', 'Probably yes', 'Not sure', 'Probably not', 'Definitely not'],
  },
];

const StarRating = ({
  value,
  onChange,
  size = 36,
}: {
  value: number;
  onChange: (v: number) => void;
  size?: number;
}) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform hover:scale-110"
        >
          <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={(hovered || value) >= star ? '#F59E0B' : 'none'}
            stroke={(hovered || value) >= star ? '#F59E0B' : '#D1D5DB'}
            strokeWidth="1.5"
          >
            <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
        </button>
      ))}
    </div>
  );
};

const ratingLabels: Record<number, string> = {
  1: 'Very Poor',
  2: 'Poor',
  3: 'Average',
  4: 'Good',
  5: 'Excellent',
};

const FeedbackSurveyInteractive = () => {
  const [step, setStep] = useState<Step>('welcome');
  const [survey, setSurvey] = useState<SurveyData>({
    overallRating: 0,
    npsScore: null,
    serviceRatings: {},
    answers: {},
    openFeedback: '',
    email: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const steps: Step[] = ['welcome', 'rating', 'nps', 'questions', 'open', 'complete'];
  const currentIndex = steps.indexOf(step);
  const progress = step === 'complete' ? 100 : Math.round((currentIndex / (steps.length - 1)) * 100);

  const handleNext = () => {
    const idx = steps.indexOf(step);
    if (idx < steps.length - 1) setStep(steps[idx + 1]);
  };

  const handleBack = () => {
    const idx = steps.indexOf(step);
    if (idx > 0) setStep(steps[idx - 1]);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    // Save to localStorage
    const submissions = JSON.parse(localStorage.getItem('feedbackSubmissions') || '[]');
    submissions.push({ ...survey, submittedAt: new Date().toISOString(), id: `FB-${Date.now()}` });
    localStorage.setItem('feedbackSubmissions', JSON.stringify(submissions));
    await new Promise((r) => setTimeout(r, 1000));
    setSubmitting(false);
    setStep('complete');
  };

  const getNPSCategory = (score: number | null) => {
    if (score === null) return null;
    if (score >= 9) return { label: 'Promoter', color: 'text-green-600', bg: 'bg-green-50' };
    if (score >= 7) return { label: 'Passive', color: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { label: 'Detractor', color: 'text-red-600', bg: 'bg-red-50' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-accent/5 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="StarIcon" size={18} className="text-primary" />
            <span className="text-sm font-bold text-primary">Customer Feedback Survey</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground mb-2">Share Your Experience</h1>
          <p className="text-muted-foreground">Help us improve your travel booking experience</p>
        </div>

        {/* Progress Bar */}
        {step !== 'complete' && (
          <div className="mb-8">
            <div className="flex justify-between text-xs font-bold text-muted-foreground mb-2">
              <span>Step {currentIndex + 1} of {steps.length - 1}</span>
              <span>{progress}% complete</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-border overflow-hidden">
          {/* Welcome Step */}
          {step === 'welcome' && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="ChatBubbleBottomCenterTextIcon" size={40} className="text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-black text-foreground mb-3">Welcome to Our Survey</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                This short survey takes about 2 minutes. Your feedback directly shapes how we improve Dial For Flight for travellers like you.
              </p>
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { icon: 'ClockIcon', label: '2 minutes', sub: 'Quick & easy' },
                  { icon: 'ShieldCheckIcon', label: 'Anonymous', sub: 'Optional email' },
                  { icon: 'SparklesIcon', label: 'Improves', sub: 'Your experience' },
                ].map((item, i) => (
                  <div key={i} className="bg-surface rounded-xl p-4 text-center">
                    <Icon name={item.icon as any} size={24} className="text-primary mx-auto mb-2" />
                    <div className="text-sm font-black text-foreground">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.sub}</div>
                  </div>
                ))}
              </div>
              <button
                onClick={handleNext}
                className="w-full py-3.5 bg-gradient-to-r from-primary to-secondary text-white font-heading font-black rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
              >
                Start Survey →
              </button>
            </div>
          )}

          {/* Overall Rating Step */}
          {step === 'rating' && (
            <div className="p-8">
              <h2 className="font-heading text-xl font-black text-foreground mb-2">Overall Experience</h2>
              <p className="text-muted-foreground mb-8">How would you rate your overall experience with Dial For Flight?</p>

              <div className="flex flex-col items-center gap-4 mb-8">
                <StarRating value={survey.overallRating} onChange={(v) => setSurvey((s) => ({ ...s, overallRating: v }))} size={48} />
                {survey.overallRating > 0 && (
                  <span className="text-lg font-black text-amber-500">{ratingLabels[survey.overallRating]}</span>
                )}
              </div>

              <div className="space-y-4 mb-8">
                <h3 className="text-sm font-black text-foreground uppercase tracking-wide">Rate specific aspects</h3>
                {serviceCategories.map((cat) => (
                  <div key={cat.key} className="flex items-center justify-between p-3 bg-surface rounded-xl">
                    <div className="flex items-center gap-3">
                      <Icon name={cat.icon as any} size={18} className="text-primary" />
                      <span className="text-sm font-bold text-foreground">{cat.label}</span>
                    </div>
                    <StarRating
                      value={survey.serviceRatings[cat.key] || 0}
                      onChange={(v) => setSurvey((s) => ({ ...s, serviceRatings: { ...s.serviceRatings, [cat.key]: v } }))}
                      size={24}
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button onClick={handleBack} className="flex-1 py-3 border border-border rounded-xl font-bold text-foreground hover:bg-surface transition-colors">
                  ← Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={survey.overallRating === 0}
                  className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* NPS Step */}
          {step === 'nps' && (
            <div className="p-8">
              <h2 className="font-heading text-xl font-black text-foreground mb-2">Net Promoter Score</h2>
              <p className="text-muted-foreground mb-8">
                On a scale of 0–10, how likely are you to recommend Dial For Flight to a friend or colleague?
              </p>

              <div className="mb-6">
                <div className="grid grid-cols-11 gap-1 mb-3">
                  {Array.from({ length: 11 }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setSurvey((s) => ({ ...s, npsScore: i }))}
                      className={`aspect-square rounded-lg text-sm font-black transition-all duration-200 hover:scale-110 ${
                        survey.npsScore === i
                          ? i >= 9
                            ? 'bg-green-500 text-white shadow-lg'
                            : i >= 7
                            ? 'bg-yellow-400 text-white shadow-lg'
                            : 'bg-red-500 text-white shadow-lg' :'bg-surface text-foreground hover:bg-muted border border-border'
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Not at all likely</span>
                  <span>Extremely likely</span>
                </div>
              </div>

              {survey.npsScore !== null && (() => {
                const cat = getNPSCategory(survey.npsScore);
                return cat ? (
                  <div className={`${cat.bg} rounded-xl p-4 mb-6 flex items-center gap-3`}>
                    <Icon name="InformationCircleIcon" size={20} className={cat.color} />
                    <div>
                      <span className={`font-black ${cat.color}`}>{cat.label}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {cat.label === 'Promoter' && 'Thank you! You love us 💚'}
                        {cat.label === 'Passive' && 'We\'d love to earn a higher score!'}
                        {cat.label === 'Detractor' && 'We\'re sorry — your feedback will help us improve.'}
                      </p>
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="flex gap-3">
                <button onClick={handleBack} className="flex-1 py-3 border border-border rounded-xl font-bold text-foreground hover:bg-surface transition-colors">
                  ← Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={survey.npsScore === null}
                  className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Multi-choice Questions Step */}
          {step === 'questions' && (
            <div className="p-8">
              <h2 className="font-heading text-xl font-black text-foreground mb-2">A Few Quick Questions</h2>
              <p className="text-muted-foreground mb-6">Help us understand your travel habits better.</p>

              <div className="space-y-6 mb-8">
                {multiChoiceQuestions.map((q) => (
                  <div key={q.key}>
                    <p className="text-sm font-black text-foreground mb-3">{q.question}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {q.options.map((opt) => (
                        <button
                          key={opt}
                          onClick={() => setSurvey((s) => ({ ...s, answers: { ...s.answers, [q.key]: opt } }))}
                          className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-bold transition-all duration-200 ${
                            survey.answers[q.key] === opt
                              ? 'border-primary bg-primary/5 text-primary' :'border-border text-foreground hover:border-primary/40 hover:bg-surface'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                              survey.answers[q.key] === opt ? 'border-primary bg-primary' : 'border-gray-300'
                            }`}>
                              {survey.answers[q.key] === opt && (
                                <div className="w-full h-full rounded-full bg-white scale-50 block" />
                              )}
                            </div>
                            {opt}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button onClick={handleBack} className="flex-1 py-3 border border-border rounded-xl font-bold text-foreground hover:bg-surface transition-colors">
                  ← Back
                </button>
                <button onClick={handleNext} className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:bg-secondary transition-colors">
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Open Feedback Step */}
          {step === 'open' && (
            <div className="p-8">
              <h2 className="font-heading text-xl font-black text-foreground mb-2">Your Thoughts</h2>
              <p className="text-muted-foreground mb-6">Any additional comments, suggestions, or things we could do better?</p>

              <textarea
                value={survey.openFeedback}
                onChange={(e) => setSurvey((s) => ({ ...s, openFeedback: e.target.value }))}
                placeholder="Tell us what you loved, what could be improved, or any specific experience you'd like to share..."
                rows={5}
                className="w-full px-4 py-3 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-6"
              />

              <div className="mb-6">
                <label className="block text-sm font-black text-foreground mb-2">Email (optional)</label>
                <input
                  type="email"
                  value={survey.email}
                  onChange={(e) => setSurvey((s) => ({ ...s, email: e.target.value }))}
                  placeholder="your@email.com — we may follow up on your feedback"
                  className="w-full px-4 py-3 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex gap-3">
                <button onClick={handleBack} className="flex-1 py-3 border border-border rounded-xl font-bold text-foreground hover:bg-surface transition-colors">
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-60"
                >
                  {submitting ? 'Submitting...' : 'Submit Feedback ✓'}
                </button>
              </div>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={44} className="text-green-500" />
              </div>
              <h2 className="font-heading text-2xl font-black text-foreground mb-3">Thank You! 🎉</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Your feedback has been recorded. We genuinely appreciate you taking the time to help us improve.
              </p>

              {/* Summary */}
              <div className="bg-surface rounded-xl p-5 text-left mb-6 space-y-3">
                <h3 className="text-sm font-black text-foreground uppercase tracking-wide mb-3">Your Feedback Summary</h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Overall Rating</span>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map((s) => (
                      <svg key={s} width={16} height={16} viewBox="0 0 24 24" fill={s <= survey.overallRating ? '#F59E0B' : '#E5E7EB'} stroke="none">
                        <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                      </svg>
                    ))}
                  </div>
                </div>
                {survey.npsScore !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">NPS Score</span>
                    <span className={`text-sm font-black ${getNPSCategory(survey.npsScore)?.color}`}>
                      {survey.npsScore}/10 — {getNPSCategory(survey.npsScore)?.label}
                    </span>
                  </div>
                )}
              </div>

              <a
                href="/homepage"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-secondary transition-colors"
              >
                <Icon name="HomeIcon" size={18} />
                Back to Homepage
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackSurveyInteractive;
